import { useCallback, useState } from "react";

/**
 * The rule over Add client and New enquiry: a workspace has to have said what
 * it sells and what it does before it can record anyone against either.
 *
 * It is a real rule rather than a nicety, and it is two things:
 *
 *   - A plan. A client is added with the subscription they arrived holding,
 *     and that subscription is one of the workspace's own plans — with none on
 *     the books the Plan field has an empty list under it. Enquiries are the
 *     same list one step earlier: what a payment is requested for is a plan, so
 *     a pipeline that cannot name one has nowhere to go.
 *   - A service. Both forms' Goal field picks from the practice's own active
 *     services and sends the service's `public_id`, so a workspace with none
 *     listed has a required field nobody can answer.
 *
 * Either one missing is a form the person cannot finish and has no way of
 * knowing why. So the answer is given at the door rather than at the field, and
 * it is given as the next step rather than as a refusal — see
 * `PlansRequiredModal` and `ServicesRequiredModal`, which are what this raises.
 *
 * One gate for both, holding one prompt: which dialog goes up is the caller's
 * to pick from what it found missing, and a workspace missing both is asked for
 * the plan first — the order the My brand tabs are in, and the order the
 * dashboard's setup checklist walks.
 *
 * This is the browser's half of it and nothing more. It stops somebody walking
 * into a form they cannot finish; it is not what enforces anything, and the
 * server is still the only thing that decides what a workspace may store.
 */

export interface SetupGate {
  /** Whether the prompt is up, which is the whole of what the page draws on. */
  prompting: boolean;
  /**
   * Wraps whatever the button was going to do. Hand it the navigation and put
   * the result on `onClick`: it runs when the workspace is ready, and raises
   * the prompt instead when something is still missing.
   *
   * A wrapper rather than a `blocked` boolean the caller branches on, because
   * both list screens carry two of these buttons and the branch has to read the
   * same way on each.
   */
  guard: (go: () => void) => () => void;
  /** Closes the prompt. What the second button and Escape both land on. */
  dismiss: () => void;
}

/**
 * `missing` is the answer to "is this workspace short of a plan or a service?",
 * worked out by the caller because the two kinds of caller already know it
 * differently: the list screens ask `usePlansCreated` and `useServicesCreated`
 * for the one bit each, and the two forms are already holding the lists their
 * own fields are drawn from and would otherwise fetch the same things twice.
 *
 * It must be false while a read is still out and false if a read failed. Not
 * knowing is not the same as knowing there is nothing there, and a prompt
 * raised over a slow call would stand in front of a dietitian who has sold
 * things for a year. The same caution the subscription gate takes.
 */
export function useSetupGate(missing: boolean): SetupGate {
  const [prompting, setPrompting] = useState(false);

  const guard = useCallback(
    (go: () => void) => () => {
      if (missing) {
        setPrompting(true);
        return;
      }
      go();
    },
    [missing]
  );

  const dismiss = useCallback(() => setPrompting(false), []);

  return { prompting, guard, dismiss };
}

export default useSetupGate;
